import React, { useEffect, useMemo, useState } from 'react'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import { useCriticalPathDragContextOptional } from './CriticalPathContext'
import { useDragAutoScroll } from '../hooks/useDragAutoScroll'
import { useDragScrollOffset } from '../hooks/useDragScrollOffset'
import { GanttColors, GanttDimensions } from '../constants'
import { FONT_FAMILY } from '../../../../../../../../../../styles/commonStyles'
import { useIntl } from 'react-intl'

interface CriticalPathDragOverlayProps {
  containerRef: React.RefObject<HTMLElement>
}

interface ContainerBounds {
  left: number
  top: number
  width: number
  height: number
}

/**
 * Overlay SVG for drawing critical path line preview during D&D.
 * Same pattern as DependencyDragOverlay but with red color.
 */
export const CriticalPathDragOverlay: React.FC<
  CriticalPathDragOverlayProps
> = ({ containerRef }) => {
  const dragContext = useCriticalPathDragContextOptional()
  const [bounds, setBounds] = useState<ContainerBounds | null>(null)
  const isDragging = !!dragContext?.dragState.isDragging
  useDragAutoScroll(containerRef, isDragging)
  const scrollOffset = useDragScrollOffset(containerRef, isDragging)

  useEffect(() => {
    const container = containerRef.current
    if (!container || !dragContext?.dragState.isDragging) {
      setBounds(null)
      return
    }

    // ヘッダーを除いた行エリアのみをカバーするため、ag-body-viewport の矩形を使用
    const bodyViewport = container.querySelector('.ag-body-viewport')
    const rect = bodyViewport
      ? bodyViewport.getBoundingClientRect()
      : container.getBoundingClientRect()
    setBounds({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    })
  }, [containerRef, dragContext?.dragState.isDragging])

  useEffect(() => {
    if (!dragContext) return

    const handleMouseMove = (e: MouseEvent) => {
      if (dragContext.dragState.isDragging) {
        dragContext.updateDragPosition(e.clientX, e.clientY)
      }
    }

    const handleMouseUp = () => {
      setTimeout(() => {
        if (dragContext.dragState.isDragging) {
          dragContext.endDrag()
        }
      }, 0)
    }

    if (dragContext.dragState.isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dragContext?.dragState.isDragging, dragContext])

  // startX/startY はドラッグ開始時の clientX/clientY なので、
  // スクロール差分を減算して内容に追従させる。
  const linePositions = useMemo(() => {
    if (!dragContext?.dragState.isDragging || !bounds) return null

    const { startX, startY, currentX, currentY } = dragContext.dragState
    return {
      startX: startX - bounds.left - scrollOffset.x,
      startY: startY - bounds.top - scrollOffset.y,
      currentX: currentX - bounds.left,
      currentY: currentY - bounds.top,
    }
  }, [dragContext?.dragState, bounds, scrollOffset])

  if (!dragContext || !bounds || !linePositions) return null

  const { startX, startY, currentX, currentY } = linePositions
  const isVisible = dragContext.dragState.isDragging

  const lineColor = GanttColors.CRITICAL_PATH_LINE
  const circleRadius = GanttDimensions.CRITICAL_PATH_CIRCLE_RADIUS + 1
  const lineWidth = GanttDimensions.CRITICAL_PATH_LINE_WIDTH

  const { fromDate, hoverDate } = dragContext.dragState

  return (
    <svg
      style={{
        position: 'fixed',
        left: bounds.left,
        top: bounds.top,
        width: bounds.width,
        height: bounds.height,
        pointerEvents: 'none',
        zIndex: 1000,
        visibility: isVisible ? 'visible' : 'hidden',
      }}
    >
      <line
        x1={startX}
        y1={startY}
        x2={currentX}
        y2={currentY}
        stroke={lineColor}
        strokeWidth={lineWidth}
        strokeDasharray="5,5"
      />
      <circle cx={startX} cy={startY} r={circleRadius} fill={lineColor} />
      <circle
        cx={currentX}
        cy={currentY}
        r={circleRadius}
        fill="none"
        stroke={lineColor}
        strokeWidth={lineWidth}
      />
      {fromDate && <TooltipText x={startX} y={startY - 15} date={fromDate} />}
      {hoverDate && (
        <TooltipText x={currentX} y={currentY - 15} date={hoverDate} />
      )}
    </svg>
  )
}

// MUI Tooltip default style values
const TOOLTIP_FONT_SIZE = 11 // 0.6875rem
const TOOLTIP_PADDING_X = 8
const TOOLTIP_PADDING_Y = 4
const TOOLTIP_LINE_HEIGHT = 14
const TOOLTIP_BG = 'rgba(97, 97, 97, 0.92)' // MUI default (#616161 with opacity)
const TOOLTIP_BORDER_RADIUS = 4
const TOOLTIP_ARROW_SIZE = 6

const TooltipText: React.FC<{
  x: number
  y: number
  date: CalendarDateVO
}> = ({ x, y, date }) => {
  const intl = useIntl()
  const title = intl.formatMessage({ id: 'gantt.criticalPath.interProcess' })
  const label = date.format('M/D (ddd)')
  const titleWidth = title.length * TOOLTIP_FONT_SIZE + TOOLTIP_PADDING_X * 2
  const dateWidth = label.length * 7 + TOOLTIP_PADDING_X * 2
  const textWidth = Math.max(titleWidth, dateWidth)
  const textHeight = TOOLTIP_LINE_HEIGHT * 2 + TOOLTIP_PADDING_Y * 2
  const arrowY = y - TOOLTIP_ARROW_SIZE
  const boxY = arrowY - textHeight

  return (
    <g>
      <rect
        x={x - textWidth / 2}
        y={boxY}
        width={textWidth}
        height={textHeight}
        rx={TOOLTIP_BORDER_RADIUS}
        fill={TOOLTIP_BG}
      />
      {/* Arrow */}
      <polygon
        points={`${x - TOOLTIP_ARROW_SIZE},${arrowY} ${x + TOOLTIP_ARROW_SIZE},${arrowY} ${x},${arrowY + TOOLTIP_ARROW_SIZE}`}
        fill={TOOLTIP_BG}
      />
      <text
        x={x}
        y={boxY + TOOLTIP_PADDING_Y + TOOLTIP_LINE_HEIGHT - 2}
        textAnchor="middle"
        fill="#ffffff"
        fontSize={TOOLTIP_FONT_SIZE}
        fontFamily={FONT_FAMILY}
      >
        {title}
      </text>
      <text
        x={x}
        y={boxY + TOOLTIP_PADDING_Y + TOOLTIP_LINE_HEIGHT * 2 - 2}
        textAnchor="middle"
        fill="#ffffff"
        fontSize={TOOLTIP_FONT_SIZE}
        fontFamily={FONT_FAMILY}
      >
        {label}
      </text>
    </g>
  )
}
